import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { Switch } from "@component/switch";
import { Settings, FileText, CheckCircle2, Circle } from "lucide-react";

export interface FormSettingsData {
    description: string;
    includeInCandidateScoring: boolean;
}

interface FormSettingsSectionProps {
    data: FormSettingsData;
    onDataChange: (data: FormSettingsData) => void;
    disabled?: boolean;
}

export default function FormSettingsSection({
    data,
    onDataChange,
    disabled = false,
}: FormSettingsSectionProps) {
    const checklistItems = [
        {
            label: "Add a form description",
            completed: !!data.description && data.description.trim().length > 0,
        },
    ];

    const completedCount = checklistItems.filter((item) => item.completed).length;
    const totalCount = checklistItems.length;
    const progressPercentage = Math.round((completedCount / totalCount) * 100);

    return (
        <div
            className="pb-8 border-b border-gray-200"
            style={{ paddingTop: "24px", paddingBottom: "40px" }}
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Panel */}
                <div className="lg:col-span-3 flex flex-col space-y-4">
                    <div className="space-y-3">
                        <div
                            className="flex items-center justify-between gap-3"
                            style={{ paddingRight: "16px" }}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="flex items-center justify-center w-10 h-10 rounded-lg"
                                    style={{ backgroundColor: "#dbeafe", color: "#2563eb" }}
                                >
                                    <Settings
                                        className="w-5 h-5"
                                        style={{ color: "#2563eb" }}
                                    />
                                </div>
                                <h2 className="text-lg font-semibold">Form Settings</h2>
                            </div>
                            <span
                                className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded"
                                style={{
                                    paddingTop: "4px",
                                    paddingBottom: "4px",
                                    borderRadius: "1rem",
                                }}
                            >
                                Step 1
                            </span>
                        </div>
                    </div>
                    <p className="text-sm text-gray-500">
                        Configure your form settings and scoring preferences.
                    </p>

                    <div className="flex-1" />

                    {/* Progress Checklist */}
                    <div className="mt-auto space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">
                                Complete your Form Settings
                            </h3>
                            <span className="text-xs text-blue-600">
                                {completedCount}/{totalCount} completed
                            </span>
                        </div>

                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>

                        <div className="space-y-2.5 pt-1">
                            {checklistItems.map((item, index) => (
                                <div key={index} className="flex items-start gap-2.5">
                                    <div className="mt-0.5">
                                        {item.completed ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                                        ) : (
                                            <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                        )}
                                    </div>
                                    <span
                                        className={`text-sm leading-tight ${item.completed
                                            ? "text-gray-700"
                                            : "text-gray-500"
                                            }`}
                                    >
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Panel */}
                <div className="lg:col-span-9 space-y-6">
                    <div className="space-y-2">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            Description
                            <span className="text-red-500">*</span>
                        </Label>
                        <p className="text-xs text-gray-500">
                            Provide a brief description of what this form is
                            about. This will be shown to candidates.
                        </p>
                        <div className="relative">
                            <Textarea
                                value={data.description}
                                onChange={(e) => {
                                    if (
                                        e.target.value.length <= 200 ||
                                        e.target.value.length < data.description.length
                                    ) {
                                        onDataChange({ ...data, description: e.target.value });
                                    }
                                }}
                                className="min-h-[100px] resize-y pr-24"
                                placeholder="Enter form description..."
                                disabled={disabled}
                            />
                            <span className="absolute right-3 bottom-3 text-xs text-gray-400 pointer-events-none">
                                {data.description.length}/200 characters
                            </span>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <Switch
                                id="scoring-toggle"
                                checked={data.includeInCandidateScoring}
                                onCheckedChange={(checked) =>
                                    onDataChange({ ...data, includeInCandidateScoring: checked })
                                }
                                className="data-[state=checked]:bg-blue-600"
                                disabled={disabled}
                            />
                            <Label
                                htmlFor="scoring-toggle"
                                className="text-base font-medium cursor-pointer"
                            >
                                Use responses in match score
                            </Label>
                        </div>
                        <p className="text-xs text-gray-500">
                            Enable scoring to evaluate candidate responses and calculate match
                            scores based on correct answers.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
